import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDevices } from '../hooks/useAgent.js';
import { useAgentStore } from '../store/agentStore.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import WatchToggle from '../components/devices/WatchToggle.jsx';
import ServicePills from '../components/shared/ServicePills.jsx';
import { displayName, displayNameSource } from '../lib/deviceDisplay.js';

import { TableSkeleton } from '../components/shared/Skeleton.jsx';
function formatIp(ip) {
  if (!ip) return '—';
  return ip.replace(/\/\d+$/, '');
}

// Filter-source switch: when health/segment filters are inactive
// the visible table sources from the paginated `devices` list (one
// page-size at a time, server cursor); when either filter is active
// it sources from the `summaryList` (limit=5000) so the operator sees
// matching rows from outside the current page (vista#239 / PR #242).
//
// Extracted as an exported pure helper so the regression test for
// the source-switch can pin the math directly without simulating
// React state changes (vista#243).
//
// Inputs:
//   devices       — current paginated page (already sorted upstream)
//   summaryList   — limit=5000 summary fetch
//   healthFilter  — 'all' | 'healthy' | 'degraded' | 'critical' | 'down' | 'noData'
//   segmentFilter — 'all' | UNASSIGNED_SEGMENT | <UUID>
//   sortCol/Dir   — column + direction for the summaryList sort (devices
//                   is pre-sorted by the parent's sort logic)
//
// Returns the filtered+sorted array the table should render.
export function selectFilteredDevices({ devices, summaryList, healthFilter, segmentFilter, serviceFilter = 'all', searchTerms = [], watchedOnly = false, sortCol, sortDir }) {
  const wantUnassignedSegment = segmentFilter === UNASSIGNED_SEGMENT;
  // vista: a real (UUID) segment selection is now filtered CLIENT-SIDE too, so
  // the segment chips (built from the full summaryList) stay visible when one is
  // selected — selecting a segment highlights its chip + narrows the list, but
  // the other segment chips remain (they'd vanish if the summary fetch were
  // server-filtered by segment, as it was pre-fix).
  const wantRealSegment = segmentFilter !== 'all' && segmentFilter !== UNASSIGNED_SEGMENT;
  const wantService = serviceFilter !== 'all';
  const wantSearch = Array.isArray(searchTerms) && searchTerms.length > 0;
  if (healthFilter === 'all' && !wantUnassignedSegment && !wantRealSegment && !wantService && !wantSearch && !watchedOnly) {
    // vista#593: sort the FULL set (summaryList), not just the current cursor
    // page — otherwise "sort by IP" only reorders the visible 25/50/100 slice
    // instead of all N devices. Fall back to the server page only until
    // summaryList (the limit=5000 fetch) has loaded.
    if (!summaryList.length) return devices;
    return [...summaryList].sort((a, b) => compareDevices(a, b, sortCol, sortDir));
  }
  const sorted = [...summaryList].sort((a, b) => compareDevices(a, b, sortCol, sortDir));
  return sorted.filter((d) => {
    // #631: watch-list view — keep only watched devices.
    if (watchedOnly && !d.watched) return false;
    if (wantUnassignedSegment && d.segment_id) return false;
    if (wantRealSegment && d.segment_id !== segmentFilter) return false;
    // vista#604: multi-term search is a client-side OR filter over the
    // summaryList — a device matches if ANY chip term is a substring of any
    // searchable field. Going client-side (rather than the single-term server
    // `search` param) is what lets multiple terms coexist as removable chips.
    if (wantSearch && !deviceMatchesTerms(d, searchTerms)) return false;
    // Service filter cuts orthogonally to health/segment. Synthetic
    // 'syslog' chip checks last_syslog_at; everything else checks
    // membership in the device's `services` array. Service entries may
    // be strings or {name, display_name} objects — the agent's
    // /api/v1/devices endpoint returns objects, fixtures often use strings.
    if (wantService) {
      if (serviceFilter === 'syslog') {
        if (!d.last_syslog_at) return false;
      } else if (
        !Array.isArray(d.services) ||
        !d.services.some((s) => (typeof s === 'string' ? s : s?.name) === serviceFilter)
      ) {
        return false;
      }
    }
    if (healthFilter === 'all') return true;
    const s = d.health_score;
    // Filters must mirror the SummaryCard count calculation's
    // priority order (down > noData > healthy > degraded > critical
    // — mutually exclusive). Without the `status !== 'down'` guards
    // a device that is both `status='down'` AND `score < 60` would
    // be counted as Down (down++ wins in the if/else priority below)
    // but still match the Critical filter, producing the "Critical: 1"
    // summary card that opens a list of many devices when clicked
    // (vista#TBD).
    if (healthFilter === 'down') return d.status === 'down';
    if (d.status === 'down') return false;
    if (healthFilter === 'noData') return s == null;
    if (s == null) return false;
    if (healthFilter === 'healthy') return s >= 80;
    if (healthFilter === 'degraded') return s >= 60 && s < 80;
    if (healthFilter === 'critical') return s < 60;
    return true;
  });
}

// UI-only sentinel for the "no segment assigned" chip. The agent's
// /api/v1/devices accepts segment_id only as a parseable UUID — passing
// any other string 400s. We use this sentinel as the chip's filter value
// and translate it to a client-side filter (devices with segment_id ==
// null) instead of forwarding it as a server query param. (vista#271)
//
// Exported so the regression test can lock the value directly rather than
// indirectly via React render output.
export const UNASSIGNED_SEGMENT = '__unassigned__';

// vista#719: segment filtering is now entirely client-side (the summary fetch is
// no longer server-filtered by segment_id, so the chips stay complete when a
// segment is selected — see selectFilteredDevices + isClientPaginated). The
// former serverSegmentId() helper decided whether to forward segment_id to the
// server; it's removed along with the server-side segment path.

// True when the visible table sources from the limit=5000 summaryList
// (client-side filter) instead of the server-paged `devices`. Status
// filter or the UNASSIGNED_SEGMENT sentinel both branch through
// summaryList — server cursor pagination doesn't apply, so the page
// footer paginates the filtered array client-side (vista#272).
export function isClientPaginated(healthFilter, segmentFilter, serviceFilter = 'all', searchTerms = [], watchedOnly = false) {
  return healthFilter !== 'all'
    || segmentFilter !== 'all'
    || serviceFilter !== 'all'
    || (Array.isArray(searchTerms) && searchTerms.length > 0)
    || watchedOnly;
}

// vista#604: multi-term Devices search. Each entered term becomes a removable
// chip; the chips filter the summaryList client-side with OR semantics. These
// pure helpers carry the term-list logic so it's unit-testable without a DOM.

// Fields a search term is matched against (case-insensitive substring). Mirrors
// the old single-term server search surface (hostname / IP / MAC / vendor) plus
// the display-name provenance columns and segment/type so a chip like "calix"
// or "core" narrows the way an operator expects.
export const SEARCH_FIELDS = [
  'hostname',
  'auto_hostname',
  'alias',
  'ip_address',
  'mac_address',
  'vendor',
  'device_type',
  'segment_name',
];

// Normalize raw input into a search term, or null if it isn't a usable term
// (empty / whitespace-only). Trimmed but case preserved for chip display;
// matching lowercases both sides.
export function normalizeSearchTerm(raw) {
  const t = String(raw ?? '').trim();
  return t.length > 0 ? t : null;
}

// Append a validated term to the list, case-insensitively de-duplicated. Returns
// the same array reference when the term is invalid or already present so the
// caller can skip a no-op state update.
export function addSearchTerm(terms, raw) {
  const t = normalizeSearchTerm(raw);
  if (!t) return terms;
  const lower = t.toLowerCase();
  if (terms.some((x) => x.toLowerCase() === lower)) return terms;
  return [...terms, t];
}

// Remove a term (case-insensitive) from the list.
export function removeSearchTerm(terms, term) {
  const lower = String(term).toLowerCase();
  return terms.filter((x) => x.toLowerCase() !== lower);
}

// OR-match a device against the term list: true if the list is empty, otherwise
// true if ANY term is a case-insensitive substring of ANY single searchable
// field. Matching is per-field (not over a joined haystack) so a term that
// contains a space can't match across a field boundary — e.g. "core 10" won't
// spuriously match a device whose hostname ends "core" and whose IP starts "10"
// (#612 review fold).
export function deviceMatchesTerms(device, terms) {
  if (!Array.isArray(terms) || terms.length === 0) return true;
  const fields = SEARCH_FIELDS.map((f) => device?.[f])
    .filter((v) => typeof v === 'string')
    .map((v) => v.toLowerCase());
  return terms.some((t) => {
    const term = String(t).toLowerCase();
    return fields.some((v) => v.includes(term));
  });
}

// Max devices the summary fetch pulls (the agent clamps here too). vista#593
// routes the unfiltered table through this (client-paginated) so sorting spans
// the whole set — but at this cap. `summaryCapped` reports when an org exceeds
// it, so the footer can show a legible "first N of M" notice instead of
// silently hiding devices 5001..M. The real server-side sort is tracked in
// #298.
export const SUMMARY_LIMIT = 5000;
export function summaryCapped(summaryCount, totalCount) {
  return summaryCount >= SUMMARY_LIMIT && totalCount > summaryCount;
}

// Total page count for a client-side-paginated array. Always at least 1
// so the footer renders "Page 1 of 1" for an empty filter-match instead
// of "Page 1 of 0".
export function totalPagesFor(itemCount, pageSize) {
  return Math.max(1, Math.ceil(itemCount / pageSize));
}

// Slice items into the page indicated by cursorIndex + pageSize. Used
// only on the client-paginated branch — the server-paginated branch
// receives one page at a time from the API and skips this helper.
export function pageSlice(items, cursorIndex, pageSize) {
  const start = cursorIndex * pageSize;
  return items.slice(start, start + pageSize);
}

// UI_GUIDELINES §3.10: pagination controls (Page N of N + First/Prev/Next)
// must not render when the list is empty or has only one page. Returns
// true only when there are 2+ pages worth of results to navigate. The
// Clear filter button is governed separately — it stays reachable in
// client-paginated mode regardless of page count (it is a recovery
// affordance, not a navigation control).
export function shouldShowPaginationControls(effectivePages) {
  return effectivePages > 1;
}

// Sort comparator shared between the paginated `devices` list and the
// filter-active `filtered` list. health_score sorts numerically with
// missing values as -1 (so unscored devices land at the bottom on
// asc); other columns sort as case-insensitive strings with missing
// values as ''.
function compareDevices(a, b, sortCol, sortDir) {
  let va = a[sortCol];
  let vb = b[sortCol];
  if (sortCol === 'health_score' || sortCol === 'seen_by_proxy_count') {
    // vista#707: numeric columns — coalesce missing to -1 so they sort
    // below real counts instead of falling into the string branch.
    va = va ?? -1;
    vb = vb ?? -1;
  } else {
    va = va ?? '';
    vb = vb ?? '';
  }
  if (typeof va === 'string') va = va.toLowerCase();
  if (typeof vb === 'string') vb = vb.toLowerCase();
  if (va < vb) return sortDir === 'asc' ? -1 : 1;
  if (va > vb) return sortDir === 'asc' ? 1 : -1;
  return 0;
}

function formatTime(ts) {
  if (!ts) return { label: '—', stale: false };
  try {
    const d = new Date(ts);
    const now = new Date();
    const secs = Math.floor((now - d) / 1000);
    let label;
    if (secs < 0) label = 'just now';
    else if (secs < 60) label = `${secs}s ago`;
    else if (secs < 3600) label = `${Math.floor(secs / 60)}m ago`;
    else if (secs < 86400) label = `${Math.floor(secs / 3600)}h ago`;
    else label = `${Math.floor(secs / 86400)}d ago`;
    return { label, stale: secs >= 60 };
  } catch {
    return { label: ts, stale: false };
  }
}

function HealthBar({ score }) {  
  if (score == null) return <span className="text-gray-400 text-xs">—</span>;
  const s = Math.round(score);
  const color = s >= 80 ? 'bg-green-500' : s >= 60 ? 'bg-yellow-500' : s >= 40 ? 'bg-orange-500' : 'bg-red-500';
  const textColor = s >= 80 ? 'text-green-700' : s >= 60 ? 'text-yellow-700' : s >= 40 ? 'text-orange-700' : 'text-red-700';
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-16 rounded-full bg-gray-200">
        <div className={`h-2 rounded-full ${color}`} style={{ width: `${s}%` }} />
      </div>
      <span className={`text-xs font-medium ${textColor}`}>{s}</span>
    </div>
  );
}

function SummaryCard({ label, value, color, onClick, active, title }) {
  // `title` becomes the native HTML tooltip (browser-rendered, no JS,
  // accessible by default). Used to explain the filter semantic of
  // each tile so operators don't have to read the source — labels
  // like "No Data" and "Services" are otherwise opaque (vista#300).
  return (
    <button
      onClick={onClick}
      title={title}
      className={`rounded-lg border p-4 text-left shadow-sm transition-all hover:shadow-md cursor-pointer ${
        active ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 bg-white'
      }`}
    >
      <p className="text-xs font-medium uppercase text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${color || 'text-gray-900'}`}>{value}</p>
    </button>
  );
}

// vista#617: resolve which agent OWNS a device row so the detail link can
// carry ?agent_id= and avoid a cross-agent "device not found". In the
// federated ("all agents") view a row carries `merged_from: [{device_id,
// agent_id}]` — the owning agent is the entry whose device_id matches this
// representative row (falling back to the first source agent). In the
// single-agent view the row has no provenance, so the owner is the currently
// selected agent (`fallbackAgentId`).
export function deviceOwningAgentId(dev, fallbackAgentId) {
  if (Array.isArray(dev?.merged_from)) {
    const own = dev.merged_from.find((m) => m.device_id === dev.device_id);
    if (own?.agent_id) return own.agent_id;
  }
  if (Array.isArray(dev?.source_agents) && dev.source_agents.length > 0) {
    return dev.source_agents[0];
  }
  return dev?.agent_id || fallbackAgentId || null;
}

// Build the device-detail path, carrying ?agent_id= when we can resolve the
// owning agent (vista#617).
export function deviceDetailPath(dev, fallbackAgentId) {
  const owner = deviceOwningAgentId(dev, fallbackAgentId);
  return owner
    ? `/devices/${dev.device_id}?agent_id=${encodeURIComponent(owner)}`
    : `/devices/${dev.device_id}`;
}

// vista#620: the list view (sort + filters + search) is mirrored into the URL
// query string so that opening a device and pressing the browser Back arrow
// restores the exact view instead of resetting to the default sort. Defaults
// are omitted to keep a pristine list URL clean.
export const VIEW_DEFAULTS = { sort: 'hostname', dir: 'asc', health: 'all', seg: 'all', svc: 'all', watched: '0' };
const VIEW_KEYS = ['sort', 'dir', 'health', 'seg', 'svc', 'q', 'watched'];

// Serialize view state → a plain {param: value} object (non-defaults only).
// searchTerms join on comma — commitSearchTerm already treats comma as a
// delimiter, so a term can never itself contain one.
export function serializeView({ sortCol, sortDir, healthFilter, segmentFilter, serviceFilter, searchTerms, watchedOnly }) {
  const p = {};
  if (sortCol && sortCol !== VIEW_DEFAULTS.sort) p.sort = sortCol;
  if (sortDir && sortDir !== VIEW_DEFAULTS.dir) p.dir = sortDir;
  if (healthFilter && healthFilter !== VIEW_DEFAULTS.health) p.health = healthFilter;
  if (segmentFilter && segmentFilter !== VIEW_DEFAULTS.seg) p.seg = segmentFilter;
  if (serviceFilter && serviceFilter !== VIEW_DEFAULTS.svc) p.svc = serviceFilter;
  if (Array.isArray(searchTerms) && searchTerms.length) p.q = searchTerms.join(',');
  if (watchedOnly) p.watched = '1';
  return p;
}

// Parse URL params (URLSearchParams) → view state, applying defaults.
export function parseView(searchParams) {
  const get = (k) => searchParams.get(k);
  const q = get('q');
  return {
    sortCol: get('sort') || VIEW_DEFAULTS.sort,
    sortDir: get('dir') || VIEW_DEFAULTS.dir,
    healthFilter: get('health') || VIEW_DEFAULTS.health,
    segmentFilter: get('seg') || VIEW_DEFAULTS.seg,
    serviceFilter: get('svc') || VIEW_DEFAULTS.svc,
    searchTerms: q ? q.split(',').filter(Boolean) : [],
    watchedOnly: get('watched') === '1',
  };
}

export default function Devices() {
  const navigate = useNavigate();
  const { agentId } = useAgentStore();
  const [searchParams, setSearchParams] = useSearchParams();
  // Parse the view from the URL once at mount; state is seeded from it so a
  // Back-navigation that lands on /devices?sort=…&health=… restores the view.
  const [initialView] = useState(() => parseView(searchParams));
  // vista#604: search is now a list of validated chip terms (OR-matched
  // client-side over summaryList), plus the in-progress text-box value. The
  // previous single debounced server-`search` param is gone — multi-term can't
  // ride one query param, and the chips filter the already-loaded summaryList.
  const [searchTerms, setSearchTerms] = useState(initialView.searchTerms);
  const [searchInput, setSearchInput] = useState('');
  const [segmentFilter, setSegmentFilter] = useState(initialView.segmentFilter);
  const [healthFilter, setHealthFilter] = useState(initialView.healthFilter);
  // Per-service chip filter (vista#300). 'all' clears; any other value
  // narrows summaryList to devices whose `services` array includes
  // the chip's service name (or for the synthetic 'syslog' chip,
  // `last_syslog_at != null`). Like segmentFilter and healthFilter,
  // any non-'all' value sources the visible table from summaryList
  // (limit=5000), not the paginated `devices` page.
  const [serviceFilter, setServiceFilter] = useState(initialView.serviceFilter);
  // #631: watch-list view — when on, the table shows only watched devices
  // (client-side filter over summaryList, like the health/segment chips).
  const [watchedOnly, setWatchedOnly] = useState(initialView.watchedOnly);
  const [sortCol, setSortCol] = useState(initialView.sortCol);
  const [sortDir, setSortDir] = useState(initialView.sortDir);
  const [showSummary, setShowSummary] = useState(true);
  // Default to 25 — must be a value present in the dropdown options below
  // (otherwise the <select> renders blank). The previous 200 default was
  // out-of-band: dropdown showed [25, 50, 100], page fetched 200 anyway,
  // and operators reading the dashboard saw no apparent page boundary
  // even though pagination was configured. (#227)
  const [pageSize, setPageSize] = useState(25);

  // Cursor stack for forward/back navigation
  const [cursorStack, setCursorStack] = useState([null]); // [null] = first page
  const [cursorIndex, setCursorIndex] = useState(0);

  // vista#620: mirror the view (sort + filters + search) into the URL so a
  // Back-navigation restores it. Replace (not push) so each sort/filter tweak
  // updates the current history entry in place rather than spamming history;
  // non-view params (e.g. a future ?tab) are preserved. Guarded on a real diff
  // to avoid a setSearchParams render loop.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    VIEW_KEYS.forEach((k) => next.delete(k));
    const desired = serializeView({ sortCol, sortDir, healthFilter, segmentFilter, serviceFilter, searchTerms, watchedOnly });
    Object.entries(desired).forEach(([k, v]) => next.set(k, v));
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [sortCol, sortDir, healthFilter, segmentFilter, serviceFilter, searchTerms, watchedOnly, searchParams, setSearchParams]);

  // Commit the current text box as a chip term. No-op (just clears the box) if
  // the term is blank or a case-insensitive duplicate. Adding/removing a term
  // resets pagination so the filtered view starts at page 1.
  const commitSearchTerm = useCallback((raw) => {
    setSearchTerms((prev) => {
      const next = addSearchTerm(prev, raw);
      if (next !== prev) {
        setCursorStack([null]);
        setCursorIndex(0);
      }
      return next;
    });
    setSearchInput('');
  }, []);

  const dropSearchTerm = useCallback((term) => {
    setSearchTerms((prev) => removeSearchTerm(prev, term));
    setCursorStack([null]);
    setCursorIndex(0);
  }, []);

  // Enter or comma commits the term; Backspace on an empty box removes the last
  // chip (standard token-input affordance).
  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitSearchTerm(e.target.value);
    } else if (e.key === 'Backspace' && e.target.value === '') {
      setSearchTerms((prev) => prev.slice(0, -1));
      setCursorStack([null]);
      setCursorIndex(0);
    }
  }, [commitSearchTerm]);

  const currentCursor = cursorStack[cursorIndex] || undefined;

  const {
    data: rawData,
    isLoading,
    isFetching,
    isPlaceholderData,
    error: rawError,
  } = useDevices(undefined, {
    cursor: currentCursor,
    limit: pageSize,
  });

  // Summary fetch: pull up to the agent's max page (5000 since vista#298)
  // so the Healthy / Degraded / Critical / Down cards reflect the full
  // filtered set instead of just the current page. Until the agent
  // returns a server-computed `summary` field, this is the cheapest way
  // to get an accurate count without forcing the operator to walk every
  // page. The 5000 ceiling matches the agent-side clamp in
  // `crates/api/src/api/devices.rs::list_devices`; deployments crossing
  // it should re-file vista#298 for an architectural answer (server-
  // computed summary + cursor for the table) rather than another raw
  // bump on either side.
  // vista: segment is now filtered client-side (like health/service), so the
  // summary fetch pulls the FULL set — that's what keeps the segment chips
  // complete when one is selected (server-filtering it collapsed the chips).
  const { data: summaryData, error: summaryError } = useDevices(undefined, {
    limit: SUMMARY_LIMIT,
  });

  // Either fetch failing means the page can't render an honest view —
  // TanStack Query keeps the previous-success data on `data` even when
  // the next refetch errors (placeholderData: (prev) => prev), so the
  // error must be surfaced explicitly or the operator sees stale data
  // with no failure indicator. Same pattern that PR #266 closed for
  // /api/v1/alerts (vista#284 generalizes vista#259).
  const error = rawError || summaryError;

  const devices = useMemo(() => {
    const unwrapped = rawData?.data ?? rawData;
    const list = Array.isArray(unwrapped) ? unwrapped : unwrapped?.devices ?? [];
    // Client-side sort (server returns ordered by device_id for cursor stability)
    return [...list].sort((a, b) => compareDevices(a, b, sortCol, sortDir));
  }, [rawData, sortCol, sortDir]);

  // Summary list — extracted from the limit=5000 fetch — feeds both the
  // SummaryCard counts (below) and the filtered-table source (next).
  const summaryList = useMemo(() => {
    const summaryUnwrapped = summaryData?.data ?? summaryData;
    return summaryUnwrapped?.devices ?? [];
  }, [summaryData]);
  const summaryCount = summaryList.length;

  // Client-side health filter (status is in Redis, not filterable server-side).
  // When 'all', source from the current paginated page (devices). When a
  // status is selected, source from summaryList — the limit=5000 fetch — so
  // clicking a SummaryCard returns the full matching set across all pages,
  // not just devices in the visible 25/50/100 slice (issue #239). Sort is
  // re-applied so the filtered view stays consistent with the current sort
  // header.
  //
  // The UNASSIGNED_SEGMENT chip ALSO needs client-side filtering because
  // the agent's segment_id query parses as a UUID — there's no magic
  // "filter to null segment" value, so we drop the server filter for that
  // case and source from summaryList here too (vista#271).
  const filtered = useMemo(
    () => selectFilteredDevices({ devices, summaryList, healthFilter, segmentFilter, serviceFilter, searchTerms, watchedOnly, sortCol, sortDir }),
    [devices, summaryList, healthFilter, segmentFilter, serviceFilter, searchTerms, watchedOnly, sortCol, sortDir],
  );

  const totalCount = rawData?.data?.total_count ?? rawData?.total_count ?? devices.length;
  const hasMore = rawData?.cursor != null;
  const nextCursor = rawData?.cursor ?? rawData?.next_cursor ?? null;
  const pageNumber = cursorIndex + 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Whether the current view is sourced from a client-side filter over
  // summaryList (limit=5000 fetch) instead of the server-paged `devices`.
  // See `isClientPaginated` helper above for the rule.
  const clientPaginated = isClientPaginated(healthFilter, segmentFilter, serviceFilter, searchTerms, watchedOnly);
  // vista#593: once the full set (summaryList) has loaded, the table paginates
  // it CLIENT-side so sorting spans every device, not just the visible cursor
  // page. clientPaginated stays filter-only (it drives the "matching" count +
  // Clear-filter button); tablePaginated drives the pagination math.
  const tablePaginated = clientPaginated || summaryList.length > 0;
  const filteredTotalPages = totalPagesFor(filtered.length, pageSize);
  const effectivePages = tablePaginated ? filteredTotalPages : totalPages;
  const showPaginationControls = shouldShowPaginationControls(effectivePages);
  const filteredPage = useMemo(() => {
    if (!tablePaginated) return filtered;
    return pageSlice(filtered, cursorIndex, pageSize);
  }, [filtered, tablePaginated, cursorIndex, pageSize]);

  // Summary stats. Prefer agent-computed `summary` (future), fall back to
  // aggregating the limit=5000 summary fetch so cards reflect the full
  // filtered set, not just the current page.
  const stats = useMemo(() => {
    const summaryUnwrapped = summaryData?.data ?? summaryData;
    // If the agent ever ships a server-computed summary block, trust it
    // verbatim — it's accurate for arbitrary device counts, not capped at 200.
    const serverSummary = summaryUnwrapped?.summary;
    // bySegment is keyed by segment_id (UUID) so chip clicks pass a
    // parseable UUID to /api/v1/devices?segment_id=… — the previous
    // name-keyed shape forwarded chip display names as the query value
    // and 400'd the server every time (vista#271). Devices with no
    // segment_id collapse under the UNASSIGNED_SEGMENT sentinel and use
    // a client-side filter instead.
    const bySegment = {};
    // Per-service chip aggregation. Each device's `services` array
    // contributes one count per service name; `last_syslog_at` triggers
    // the synthetic 'syslog' bucket. Counts are operator-facing — a
    // device running both ssh and http appears in both ssh's count and
    // http's count (vista#300 chip replacement for the umbrella
    // "Services" tile).
    const byService = {};
    let healthy = 0, degraded = 0, critical = 0, down = 0, noData = 0;
    for (const d of summaryList) {
      const id = d.segment_id || UNASSIGNED_SEGMENT;
      const name = d.segment_name || 'unassigned';
      const entry = bySegment[id];
      if (entry) {
        entry.count += 1;
      } else {
        bySegment[id] = { id, name, count: 1 };
      }
      const score = d.health_score;
      // Down = device.status reports down (separate from score buckets).
      // No data = score is absent (fresh discovery, proxy not polling yet).
      // Lumping these together mislabeled unknown-type but up devices as
      // "Down" — see vista#181.
      if (d.status === 'down') down++;
      else if (score == null) noData++;
      else if (score >= 80) healthy++;
      else if (score >= 60) degraded++;
      else critical++;
      if (Array.isArray(d.services)) {
        for (const svc of d.services) {
          // Agent returns objects ({name, display_name}); fixtures often use bare strings.
          const name = typeof svc === 'string' ? svc : svc?.name;
          if (typeof name === 'string' && name.length > 0) {
            byService[name] = (byService[name] || 0) + 1;
          }
        }
      }
      if (d.last_syslog_at) {
        byService.syslog = (byService.syslog || 0) + 1;
      }
    }
    if (serverSummary) {
      return {
        total: totalCount,
        pageTotal: devices.length,
        bySegment,
        byService,
        healthy: serverSummary.healthy ?? healthy,
        degraded: serverSummary.degraded ?? degraded,
        critical: serverSummary.critical ?? critical,
        down: serverSummary.down ?? down,
        noData: serverSummary.no_data ?? noData,
      };
    }
    return { total: totalCount, pageTotal: devices.length, bySegment, byService, healthy, degraded, critical, down, noData };
  }, [summaryData, summaryList, devices.length, totalCount]);

  function goNextPage() {
    // Client-paginated branch: increment within filtered.length / pageSize.
    // Server cursor not advanced — summaryList already holds the (capped)
    // filtered set.
    if (tablePaginated) {
      if (cursorIndex + 1 >= filteredTotalPages) return;
      setCursorIndex(cursorIndex + 1);
      return;
    }
    if (!hasMore || !nextCursor) return;
    const newStack = [...cursorStack];
    // If we're at the end of the stack, push a new cursor
    if (cursorIndex === cursorStack.length - 1) {
      newStack.push(nextCursor);
    }
    setCursorStack(newStack);
    setCursorIndex(cursorIndex + 1);
  }

  function goPrevPage() {
    if (cursorIndex <= 0) return;
    setCursorIndex(cursorIndex - 1);
  }

  function goFirstPage() {
    setCursorIndex(0);
  }

  function changePageSize(newSize) {
    setPageSize(newSize);
    setCursorStack([null]);
    setCursorIndex(0);
  }

  // Both filter wrappers early-return on no-op transitions to avoid a
  // spurious cursor reset when the operator clicks an already-active
  // control. Without this guard, clicking the active Total card while
  // on Page 5 of all-devices would silently jump back to Page 1
  // (setCursorIndex(0) fires even though setHealthFilter is a no-op for
  // same-value updates) — vista#286 review fold.
  function changeSegmentFilter(seg) {
    if (seg === segmentFilter) return;
    setSegmentFilter(seg);
    setCursorStack([null]);
    setCursorIndex(0);
  }

  // Same no-op-guard pattern for the per-service chip filter (vista#300).
  function changeServiceFilter(svc) {
    if (svc === serviceFilter) return;
    setServiceFilter(svc);
    setCursorStack([null]);
    setCursorIndex(0);
  }

  // Cursor must reset when toggling the client-side filter on or off —
  // otherwise a cursorIndex from a deep server-paginated page (e.g. page 7
  // of all devices) lands on a non-existent slice of the filtered array
  // (which is short and entirely client-side). PR #242 sidestepped this
  // by hiding pagination when filter was active; under #272 we render the
  // pagination footer in both modes, so the reset has to be explicit.
  function changeHealthFilter(filter) {
    if (filter === healthFilter) return;
    setHealthFilter(filter);
    setCursorStack([null]);
    setCursorIndex(0);
  }

  function handleSort(col) {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  }

  function SortHeader({ col, children }) {  
    const active = sortCol === col;
    const arrow = active ? (sortDir === 'asc' ? ' \u2191' : ' \u2193') : '';
    return (
      <th
        className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 cursor-pointer select-none hover:text-gray-700"
        onClick={() => handleSort(col)}
      >
        {children}{arrow}
      </th>
    );
  }

  if (isLoading && !isPlaceholderData) return <TableSkeleton rows={8} cols={6} message="Loading devices…" />;

  // vista#284: when /api/v1/devices errors (auth expiry, agent disconnect,
  // 5xx, malformed query — anything that stops the fetch from succeeding),
  // don't fall through to the empty-state or render the previous-success
  // data verbatim. TanStack Query holds prior `data` while `error` is set
  // (placeholderData: (prev) => prev), and the operator has no way to know
  // they're looking at stale rows. Surface the error explicitly. Same
  // pattern as vista#259 / PR #266 closed for /api/v1/alerts. Cause-neutral
  // hint — the inline `error.body.error || error.message` carries the
  // diagnostic specificity.
  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Devices</h1>
        <div className="rounded-md border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-sm font-medium text-red-800">Failed to load devices</p>
          <p className="mt-1 text-xs text-red-700">
            {error?.body?.error || error?.message || 'Unknown error'}
          </p>
          <p className="mt-3 text-xs text-red-600">
            Try refreshing. If the problem persists, contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  // S14-3 Theme-1 completion: surface partial_failures from the cloud
  // federation envelope. When the worker fans out across agents and
  // one is unreachable, partial_failures carries the per-agent error
  // so the operator knows their list view is incomplete.
  const partialFailures = rawData?.partial_failures || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Devices</h1>
        <button
          onClick={() => setShowSummary((s) => !s)}
          className="text-sm text-blue-600 hover:underline"
        >
          {showSummary ? 'Hide summary' : 'Show summary'}
        </button>
      </div>

      {/* S14-3 Theme-1 completion: partial_failures banner.
          Cross-agent federation surfaces per-agent reachability here. */}
      {partialFailures.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <div className="font-medium">Partial data — one or more agents unreachable</div>
          <ul className="mt-1 space-y-0.5 text-xs">
            {partialFailures.map((pf, i) => (
              <li key={i}>
                <span className="font-mono">{pf.agent_name || (pf.agent_id || '').slice(0, 8)}</span>: {pf.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Cap-note: surface unconditionally (not just when a filter is active)
          so an operator on a >200-device deployment sees the explanation for
          card sums that don't equal Total. Long-term fix is a server-computed
          summary block (sprint-13). */}
      {showSummary && totalCount > summaryCount && (
        <p className="text-xs text-amber-600">
          Summary cards aggregate the first {summaryCount} of {totalCount} devices (agent's per-page max). The remaining {totalCount - summaryCount} aren't reflected in the per-status counts.
        </p>
      )}

      {/* Summary Cards */}
      {showSummary && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <SummaryCard label="Total" value={stats.total} active={healthFilter === 'all'} onClick={() => changeHealthFilter('all')}
            title="All discovered devices on this agent" />
          <SummaryCard label="Healthy" value={stats.healthy} color="text-green-600" active={healthFilter === 'healthy'} onClick={() => changeHealthFilter(healthFilter === 'healthy' ? 'all' : 'healthy')}
            title="Health score ≥ 80" />
          <SummaryCard label="Degraded" value={stats.degraded} color="text-yellow-600" active={healthFilter === 'degraded'} onClick={() => changeHealthFilter(healthFilter === 'degraded' ? 'all' : 'degraded')}
            title="Health score 60–79" />
          <SummaryCard label="Critical" value={stats.critical} color="text-red-600" active={healthFilter === 'critical'} onClick={() => changeHealthFilter(healthFilter === 'critical' ? 'all' : 'critical')}
            title="Health score < 60 (excludes Down)" />
          <SummaryCard label="Down" value={stats.down} color="text-gray-500" active={healthFilter === 'down'} onClick={() => changeHealthFilter(healthFilter === 'down' ? 'all' : 'down')}
            title="Status = down (no recent ICMP response)" />
          <SummaryCard label="No Data" value={stats.noData} color="text-gray-400" active={healthFilter === 'noData'} onClick={() => changeHealthFilter(healthFilter === 'noData' ? 'all' : 'noData')}
            title="Not yet scored — fresh discovery, prober not yet polled" />
        </div>
      )}

      {/* Segment breakdown — chip click sets segmentFilter to seg.id (UUID
          or UNASSIGNED_SEGMENT sentinel), display uses seg.name. The
          previous shape passed seg.name to setSegmentFilter, which then
          flowed into segment_id= and 400'd the agent (vista#271). */}
      {showSummary && Object.keys(stats.bySegment).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.values(stats.bySegment).sort((a, b) => b.count - a.count).map((seg) => (
            <button
              key={seg.id}
              onClick={() => changeSegmentFilter(segmentFilter === seg.id ? 'all' : seg.id)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                segmentFilter === seg.id
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`}
            >
              {seg.name} ({seg.count})
            </button>
          ))}
        </div>
      )}

      {/* Per-service filter chips (vista#300). Replaces the prior umbrella
          "Services" tile — operators can now narrow to devices speaking a
          specific service (e.g. only ssh hosts, only http hosts) instead
          of the binary "any service" cut. Indigo border distinguishes
          from segment chips visually. */}
      {showSummary && Object.keys(stats.byService).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(stats.byService)
            .sort(([, a], [, b]) => b - a)
            .map(([svc, count]) => (
              <button
                key={svc}
                onClick={() => changeServiceFilter(serviceFilter === svc ? 'all' : svc)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  serviceFilter === svc
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
              >
                {svc} ({count})
              </button>
            ))}
        </div>
      )}

      {/* Search Bar — multi-term chips (vista#604). Press Enter or comma to
          turn the text into a removable badge; chips OR-match across fields. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-1 min-w-[200px] flex-wrap items-center gap-1.5 rounded-lg border border-gray-200 px-2 py-1.5 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
          {searchTerms.map((term) => (
            <span
              key={term}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
            >
              {term}
              <button
                type="button"
                onClick={() => dropSearchTerm(term)}
                aria-label={`Remove ${term}`}
                className="text-blue-400 hover:text-blue-700"
              >
                ×
              </button>
            </span>
          ))}
          <input
            type="text"
            placeholder={searchTerms.length === 0 ? 'Search hostname, IP, MAC, vendor...' : 'Add term...'}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            onBlur={(e) => commitSearchTerm(e.target.value)}
            className="flex-1 min-w-[120px] border-0 px-1 py-0.5 text-sm focus:outline-none focus:ring-0"
          />
        </div>
        <span className="text-sm text-gray-500">
          {totalCount} device{totalCount !== 1 ? 's' : ''}
          {isFetching && !isLoading ? <span className="ml-2 text-blue-500 text-xs">updating...</span> : ''}
        </span>
        {/* #631: watch-list view toggle. Filters the table to watched
            devices (those probed at the faster ICMP cadence). */}
        <button
          type="button"
          aria-pressed={watchedOnly}
          onClick={() => setWatchedOnly((v) => !v)}
          title="Show only watched devices"
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
            watchedOnly
              ? 'border-amber-300 bg-amber-50 text-amber-700'
              : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
          }`}
        >
          <svg
            className="h-3.5 w-3.5"
            viewBox="0 0 24 24"
            fill={watchedOnly ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
          Watched
        </button>
      </div>

      {/* Device Table */}
      {filtered.length === 0 && !isFetching ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-sm text-gray-400">
            {totalCount === 0 ? 'No devices discovered yet.' : 'No devices match your filter.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortHeader col="hostname">Hostname</SortHeader>
                <SortHeader col="ip_address">IP</SortHeader>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">MAC</th>
                <SortHeader col="device_type">Type</SortHeader>
                <SortHeader col="segment_name">Segment</SortHeader>
                {/* vista#707: multi-vantage — how many proxies observe this device. */}
                <SortHeader col="seen_by_proxy_count">Proxies</SortHeader>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                <SortHeader col="health_score">Health</SortHeader>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Services</th>
                <SortHeader col="last_seen">Last Seen</SortHeader>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredPage.map((dev) => (
                <tr
                  key={dev.device_id}
                  onClick={() => navigate(deviceDetailPath(dev, agentId))}
                  className="cursor-pointer transition-colors hover:bg-blue-50"
                >
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {/* Migration 062 (hostname refactor): render via
                        displayName() precedence. The rendered string
                        is gray + italic when the source is the
                        agent-synthesized auto_hostname (i.e. neither
                        operator nor the network has provided a real
                        name yet), so the table still telegraphs "this
                        device hasn't reported a hostname" without the
                        old `startsWith('discovered-')` substring
                        check. #631: leading star toggles the watch list. */}
                    <span className="inline-flex items-center gap-1.5">
                      <WatchToggle device={dev} agentId={deviceOwningAgentId(dev, agentId)} />
                      {(() => {
                        const source = displayNameSource(dev);
                        const synthesized = source === 'auto_hostname' || source === 'fallback';
                        return synthesized
                          ? <span className="text-gray-400 italic">{displayName(dev)}</span>
                          : displayName(dev);
                      })()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-700">{formatIp(dev.ip_address)}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-400">{dev.mac_address || '—'}</td>
                  <td className="px-4 py-3 text-sm">
                    <StatusBadge status={dev.device_type || 'unknown'} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{dev.segment_name || '—'}</td>
                  {/* vista#707: multi-vantage proxy count. Zero renders as an
                      em-dash; a positive count as a blue pill. Detail page
                      lists the actual proxies + per-vantage latency. */}
                  <td className="px-4 py-3 text-sm">
                    {dev.seen_by_proxy_count > 0 ? (
                      <span
                        className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                        title={`Observed by ${dev.seen_by_proxy_count} prox${dev.seen_by_proxy_count === 1 ? 'y' : 'ies'}`}
                      >
                        {dev.seen_by_proxy_count}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <StatusBadge status={dev.live_status || 'unknown'} />
                  </td>
                  <td className="px-4 py-3"><HealthBar score={dev.health_score} /></td>
                  <td className="px-4 py-3">
                    <ServicePills
                      services={dev.services || []}
                      hasSyslog={!!dev.last_syslog_at}
                      snmpProfileId={dev.snmp_profile_id}
                    />
                  </td>
                  {(() => {
                    const t = formatTime(dev.last_seen);
                    // vista#616: an unmonitored device is *expected* to be stale
                    // (nobody is probing it) — don't flag its Last Seen red, that
                    // would re-create the "looks dead" impression the slate
                    // status badge is meant to dispel.
                    const alarmStale = t.stale && dev.live_status !== 'unmonitored';
                    return (
                      <td className={`px-4 py-3 text-sm ${alarmStale ? 'font-medium text-red-600' : 'text-gray-500'}`}>{t.label}</td>
                    );
                  })()}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Footer — single shape across server-paged and
          client-paged views. PR #242 previously hid pagination entirely
          in the client-paged branch, which forced the operator to scroll
          one tall list of 137+ rows; we now slice `filtered` by pageSize
          the same way (vista#272).

          UI_GUIDELINES §3.10: "Page N of N" + First/Prev/Next must NOT
          render when there's only one page. The Clear filter button is
          a recovery affordance that must remain reachable in
          client-paginated mode regardless of page count, so the outer
          gate is `controls || clientPaginated`. Inside, the page
          indicator and nav buttons gate on `showPaginationControls`
          alone (vista#286 review fold C). */}
      {(showPaginationControls || clientPaginated) && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <span>Show</span>
            <select
              value={pageSize}
              onChange={(e) => changePageSize(Number(e.target.value))}
              className="rounded border border-gray-200 px-2 py-1 text-sm"
            >
              {[25, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span>per page</span>
          </div>
          {showPaginationControls && (
            <span>
              Page {pageNumber} of {effectivePages}
              {clientPaginated ? (
                <>
                  <span className="ml-1">({filtered.length} matching)</span>
                  {totalCount > summaryCount && (
                    <span className="ml-2 text-amber-600">
                      (filter sample capped at first {summaryCount} of {totalCount} total)
                    </span>
                  )}
                </>
              ) : (
                <>
                  {totalCount > 0 && <span className="ml-1">({totalCount} total)</span>}
                  {/* vista#593 review: the unfiltered view client-paginates the
                      capped summaryList, so beyond the cap devices would vanish
                      silently. Surface it as a legible limit instead. */}
                  {summaryCapped(summaryCount, totalCount) && (
                    <span className="ml-2 text-amber-600">
                      (showing the first {summaryCount} of {totalCount} — narrow with a filter or search; full sort: #298)
                    </span>
                  )}
                </>
              )}
            </span>
          )}
          <div className="flex items-center gap-1">
            {showPaginationControls && (
              <>
                <button
                  onClick={goFirstPage}
                  disabled={cursorIndex === 0}
                  className="rounded border border-gray-200 px-2 py-1 text-xs disabled:opacity-30"
                >First</button>
                <button
                  onClick={goPrevPage}
                  disabled={cursorIndex === 0}
                  className="rounded border border-gray-200 px-2 py-1 text-xs disabled:opacity-30"
                >Prev</button>
                <button
                  onClick={goNextPage}
                  disabled={tablePaginated ? cursorIndex + 1 >= filteredTotalPages : !hasMore}
                  className="rounded border border-gray-200 px-2 py-1 text-xs disabled:opacity-30"
                >Next</button>
              </>
            )}
            {clientPaginated && (
              <button
                onClick={() => {
                  changeHealthFilter('all');
                  if (segmentFilter === UNASSIGNED_SEGMENT) changeSegmentFilter('all');
                  if (serviceFilter !== 'all') changeServiceFilter('all');
                  if (searchTerms.length > 0) {
                    setSearchTerms([]);
                    setCursorStack([null]);
                    setCursorIndex(0);
                  }
                }}
                className="ml-2 rounded border border-gray-200 px-2 py-1 text-xs hover:bg-gray-50"
              >Clear filter</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
